import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import SiteFooter from '@/components/SiteFooter';
import ListingCard from '@/components/ListingCard';
import api, { getImageUrl } from '@/api/client';
import { useSiteContent, Reveal, SectionLabel } from '@/components/Editorial';
import {
  ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight,
  ShieldCheck, Users, Scale, Globe,
  LifeBuoy, Lock, Sparkles, Check, Apple, Play,
} from 'lucide-react';

const APP_LINKS = {
  play: 'https://play.google.com/store/apps/details?id=com.app.messums',
  appstore: 'https://apps.apple.com/in/app/the-listing-room/id6749255320',
};

// Icons mapped by index — cannot be stored in DB
const VALUE_ICONS = [ShieldCheck, Users, Scale, Globe];
const SERVICE_ICONS = [LifeBuoy, Lock, Sparkles];

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

function HeroCarousel({ banners }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const count = banners.length;
  const go = useCallback((n) => setIdx((p) => (n + count) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  const handleCta = (link) => {
    if (!link) return;
    if (link.startsWith('http')) window.open(link, '_blank');
    else navigate(link);
  };

  if (count === 0) return <div style={{ height: '88vh', backgroundColor: '#0A0A0A' }} />;

  return (
    <section className="relative overflow-hidden" style={{ height: '88vh', minHeight: '600px', backgroundColor: '#0A0A0A' }} data-testid="hero-carousel">
      {banners.map((b, i) => (
        <div key={b.id || i} className="absolute inset-0 transition-opacity duration-[1000ms] ease-in-out" style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? 'auto' : 'none' }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${getImageUrl(b.image_url)}')`, transform: i === idx ? 'scale(1.06)' : 'scale(1)', transition: 'transform 7s ease-out' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.35) 55%, rgba(10,10,10,0.15) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.6) 0%, rgba(10,10,10,0) 40%)' }} />
        </div>
      ))}

      <div className="absolute top-28 right-6 md:right-12 z-10 text-white text-sm tabular-nums tracking-widest">
        <span className="text-lg font-semibold">{String(idx + 1).padStart(2, '0')}</span>
        <span className="opacity-50"> / {String(count).padStart(2, '0')}</span>
      </div>

      <div className="relative h-full container-app flex flex-col justify-end pb-24 md:pb-28">
        <div style={{ maxWidth: '1120px' }}>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-5 text-white/80">Messums Art Market — Est. 2024</p>
          </Reveal>
          <h1 key={idx} className="reveal is-visible font-bold mb-10 text-white" style={{ fontSize: 'clamp(2rem, 4.2vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.025em' }}>
            {banners[idx]?.heading}
          </h1>
          <div className="flex flex-wrap gap-4">
            {banners[idx]?.primary_label && (
              <button onClick={() => handleCta(banners[idx].primary_link)} data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 px-12 py-5 text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
                style={{ backgroundColor: '#fff', color: '#000', minWidth: '220px' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; }}>
                {banners[idx].primary_label} <ArrowRight size={16} />
              </button>
            )}
            {banners[idx]?.secondary_label && (
              <button onClick={() => handleCta(banners[idx].secondary_link)} data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-3 px-12 py-5 text-sm font-semibold uppercase tracking-wider transition-colors duration-300 text-white"
                style={{ border: '1px solid rgba(255,255,255,0.5)', backgroundColor: 'transparent', minWidth: '220px' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#fff'; }}>
                {banners[idx].secondary_label}
              </button>
            )}
          </div>
        </div>
      </div>

      {count > 1 && (
        <>
          <div className="absolute bottom-10 right-6 md:right-12 z-10 flex gap-2">
            <button onClick={() => go(idx - 1)} aria-label="Previous" data-testid="hero-prev" className="w-12 h-12 flex items-center justify-center text-white transition-colors hover:bg-white hover:text-black" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => go(idx + 1)} aria-label="Next" data-testid="hero-next" className="w-12 h-12 flex items-center justify-center text-white transition-colors hover:bg-white hover:text-black" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="absolute bottom-12 left-6 md:left-12 z-10 flex gap-2">
            {banners.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} className="h-px transition-all duration-300" style={{ width: i === idx ? '40px' : '16px', backgroundColor: i === idx ? '#fff' : 'rgba(255,255,255,0.4)' }} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function MarqueeBar({ items = [] }) {
  const doubled = [...items, ...items];
  return (
    <div className="marquee-wrap overflow-hidden py-5" style={{ backgroundColor: '#0A0A0A' }} data-testid="marquee">
      <div className="marquee-track">
        {[0, 1].map(rep => (
          <div key={rep} className="inline-flex items-center">
            {doubled.map((m, i) => (
              <span key={`${rep}-${i}`} className="inline-flex items-center text-white text-sm uppercase tracking-[0.2em]">
                <span className="px-8">{m}</span>
                <span className="opacity-40">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [banners, setBanners] = useState([]);
  const c = useSiteContent('landing');

  useEffect(() => {
    api.get('/public/hero-banners').then(r => setBanners(r.data.banners || [])).catch(() => {});
    api.get('/listings?limit=12').then(r => {
      const all = r.data.listings || [];
      const featured = all.filter(l => l.featured);
      const rest = all.filter(l => !l.featured);
      setListings([...featured, ...rest].slice(0, 6));
    }).catch(() => {}).finally(() => setLoadingListings(false));
  }, []);

  // Show hero while content loads to avoid full blank page
  if (!c) {
    return (
      <div style={{ backgroundColor: '#fff', minHeight: '100vh', color: '#000' }}>
        <Navbar />
        <HeroCarousel banners={banners} />
      </div>
    );
  }

  const marqueeItems = c.marquee || ['Zero Commission', 'Members Only', 'Vetted Provenance', 'Direct Exchange', 'Curated Selection'];

  return (
    <div style={{ backgroundColor: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />
      <HeroCarousel banners={banners} />
      <MarqueeBar items={marqueeItems} />

      {/* Mission */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal><SectionLabel index="01">{c.mission.label}</SectionLabel></Reveal>
            <Reveal delay={80}>
              <h2 className="font-bold" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: 1.02, letterSpacing: '-0.03em' }}>
                {c.mission.heading}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={160}>
              <div className="space-y-5" style={{ maxWidth: '420px' }}>
                <p className="text-base leading-relaxed" style={{ color: '#444' }}>{c.mission.p1}</p>
                <p className="text-base leading-relaxed" style={{ color: '#444' }}>{c.mission.p2}</p>
                <Link to="/browse" className="editorial-link inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider">
                  {c.mission.link_label} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Listing Highlights */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <Reveal><SectionLabel index="02">{c.highlights.label}</SectionLabel></Reveal>
              <Reveal delay={80}>
                <h2 className="font-bold" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
                  {c.highlights.heading}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <Link to="/browse" className="editorial-link text-sm font-semibold uppercase tracking-wider self-start md:self-auto">Browse all →</Link>
            </Reveal>
          </div>
          {loadingListings ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} style={{ height: '420px', backgroundColor: '#F5F5F5' }} />)}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-listings-grid">
              {listings.map((l, i) => (
                <Reveal key={l.id} delay={(i % 3) * 90}>
                  <ListingCard listing={l} featured={l.featured} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" style={{ border: '1px solid #E5E5E5' }}>
              <p className="text-2xl mb-2 font-bold">The catalogue is being curated</p>
              <p className="text-sm mb-6" style={{ color: '#666' }}>Be among the first to list your works.</p>
              <button onClick={() => navigate('/register')} className="px-7 py-3.5 text-sm font-semibold uppercase tracking-wider" style={{ backgroundColor: '#000', color: '#fff' }}>
                Apply to list
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Pricing & Services */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app grid lg:grid-cols-2">
          <Reveal className="py-24 md:py-32 lg:pr-16 lg:border-r" style={{ borderColor: '#E5E5E5' }}>
            <SectionLabel index="03">{c.pricing.label}</SectionLabel>
            <h2 className="font-bold mb-6" style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.98, letterSpacing: '-0.03em' }}>
              {c.pricing.heading_line1}<br />{c.pricing.heading_line2}
            </h2>
            <p className="text-base leading-relaxed mb-3" style={{ color: '#444', maxWidth: '440px' }}>{c.pricing.p1}</p>
            <p className="text-sm font-semibold uppercase tracking-wider mb-8">{c.pricing.tagline}</p>
            <button onClick={() => navigate('/register')}
              className="inline-flex items-center gap-3 px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
              style={{ backgroundColor: '#000', color: '#fff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
              {c.pricing.cta} <ArrowRight size={16} />
            </button>
          </Reveal>
          <Reveal delay={120} className="lg:pl-16 lg:py-32 py-12">
            <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: '#666' }}>Additional Services</p>
            <p className="text-sm mb-8" style={{ color: '#666', maxWidth: '440px' }}>{c.pricing.services_intro}</p>
            <div className="space-y-4">
              {(c.pricing.services || []).map((s, i) => {
                const Icon = SERVICE_ICONS[i] || LifeBuoy;
                return (
                  <div key={s.name} className="bento-cell p-6" style={{ border: '1px solid #E5E5E5' }}>
                    <div className="flex items-start gap-4">
                      <span className="bento-icon w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ border: '1px solid #000' }}>
                        <Icon size={18} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <p className="text-lg font-bold leading-none">{s.name}</p>
                            <p className="text-[11px] uppercase tracking-[0.15em] mt-1.5 bento-muted" style={{ color: '#999' }}>{s.tag}</p>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <span className="text-2xl font-bold tabular-nums">{s.price}</span>
                            <span className="block text-[10px] uppercase tracking-wider bento-muted" style={{ color: '#999' }}>per listing</span>
                          </div>
                        </div>
                        <p className="text-sm mt-3 bento-muted" style={{ color: '#666' }}>{s.desc}</p>
                        <ul className="mt-4 space-y-1.5">
                          {(s.features || []).map(f => (
                            <li key={f} className="flex items-center gap-2 text-xs bento-muted" style={{ color: '#444' }}>
                              <Check size={13} className="flex-shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Value Props */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal><SectionLabel index="04">{c.value.label}</SectionLabel></Reveal>
          <Reveal delay={80}>
            <h2 className="font-bold mb-14" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)', lineHeight: 1.05, letterSpacing: '-0.03em', maxWidth: '900px' }}>
              {c.value.heading}
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ borderLeft: '1px solid #E5E5E5', borderTop: '1px solid #E5E5E5' }}>
            {(c.value.items || []).map((v, i) => {
              const Icon = VALUE_ICONS[i] || ShieldCheck;
              return (
                <Reveal key={v.title} delay={(i % 4) * 80}>
                  <div className="bento-cell p-8 h-full" style={{ borderRight: '1px solid #E5E5E5', borderBottom: '1px solid #E5E5E5', minHeight: '280px' }}>
                    <div className="flex items-start justify-between mb-10">
                      <span className="bento-icon w-16 h-16 flex items-center justify-center" style={{ border: '1px solid #000' }}>
                        <Icon size={30} strokeWidth={1.4} />
                      </span>
                      <span className="text-xs font-semibold tabular-nums bento-muted" style={{ color: '#999' }}>0{i + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold mb-3">{v.title}</h3>
                    <p className="text-sm leading-relaxed bento-muted" style={{ color: '#666' }}>{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal><SectionLabel index="05">{c.testimonials.label}</SectionLabel></Reveal>
              <Reveal delay={80}>
                <h2 className="font-bold" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
                  {c.testimonials.heading}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <p className="text-sm md:text-right" style={{ color: '#666', maxWidth: '280px' }}>
                {c.testimonials.intro}
              </p>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3" style={{ borderTop: '1px solid #000', borderLeft: '1px solid #E5E5E5' }}>
            {(c.testimonials.items || []).map((t, i) => (
              <Reveal key={i} delay={i * 100}>
                <figure className="bento-cell h-full flex flex-col p-8 md:p-10" style={{ borderRight: '1px solid #E5E5E5', borderBottom: '1px solid #E5E5E5' }}>
                  <span className="font-bold mb-6 bento-muted" style={{ fontSize: '4.5rem', lineHeight: 0.5, color: '#D4D4D4' }} aria-hidden>"</span>
                  <blockquote className="text-lg leading-relaxed mb-10 flex-1" style={{ letterSpacing: '-0.01em' }}>{t.text}</blockquote>
                  <figcaption className="flex items-center gap-3 pt-6" style={{ borderTop: '1px solid #E5E5E5' }}>
                    <span className="mono w-11 h-11 flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: '#000', color: '#fff' }}>
                      {initials(t.name)}
                    </span>
                    <div>
                      <p className="text-sm font-bold">{t.name}</p>
                      <p className="text-[11px] uppercase tracking-[0.15em] bento-muted" style={{ color: '#888' }}>{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* App Download */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs font-semibold tabular-nums">(06)</span>
              <span className="w-8 h-px bg-white/40" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/50">{c.app.label}</span>
            </div>
            <h2 className="font-bold mb-6" style={{ fontSize: 'clamp(2rem, 4.2vw, 3.5rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              {c.app.heading}
            </h2>
            <p className="text-base mb-8 text-white/60" style={{ maxWidth: '420px' }}>{c.app.p}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-10">
              {(c.app.features || []).map(f => (
                <span key={f} className="inline-flex items-center gap-2 text-sm text-white/80">
                  <Check size={14} /> {f}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={APP_LINKS.appstore} target="_blank" rel="noreferrer"
                className="store-btn group inline-flex items-center gap-3 px-6 py-3.5 transition-colors duration-300"
                style={{ border: '1px solid rgba(255,255,255,0.25)' }} data-testid="app-store-btn">
                <Apple size={26} className="store-ico" fill="currentColor" strokeWidth={0} />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-wider store-sub text-white/60">Download on the</span>
                  <span className="block text-base font-bold -mt-0.5">App Store</span>
                </span>
              </a>
              <a href={APP_LINKS.play} target="_blank" rel="noreferrer"
                className="store-btn group inline-flex items-center gap-3 px-6 py-3.5 transition-colors duration-300"
                style={{ border: '1px solid rgba(255,255,255,0.25)' }} data-testid="play-store-btn">
                <Play size={24} className="store-ico" fill="currentColor" strokeWidth={0} />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-wider store-sub text-white/60">Get it on</span>
                  <span className="block text-base font-bold -mt-0.5">Google Play</span>
                </span>
              </a>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 flex justify-center items-center">
            <img src="https://www.thelistingroom.com/assets/images/app-download-2.webp" alt="App preview" className="w-full"
              style={{ maxHeight: '680px', objectFit: 'contain', filter: 'grayscale(1) contrast(1.05)', transform: 'scale(1.08)' }} />
          </Reveal>
        </div>
      </section>

      {/* Join CTA */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://www.thelistingroom.com/assets/images/Group-12.jpg')", filter: 'grayscale(1)', opacity: 0.5 }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(10,10,10,0.25) 0%, rgba(10,10,10,0.72) 100%)' }} />
        <div className="relative container-app py-28 md:py-36 text-center" style={{ color: '#fff' }}>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-6 text-white/55">{c.join.label}</p>
            <h2 className="font-bold mx-auto mb-5" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4.5rem)', lineHeight: 0.95, letterSpacing: '-0.035em' }}>
              {c.join.heading}
            </h2>
            <p className="text-base text-white/65 mx-auto mb-12" style={{ maxWidth: '520px' }}>{c.join.p}</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-px max-w-3xl mx-auto mb-12 text-left" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              {(c.join.benefits || []).map((b) => (
                <div key={b} className="flex items-center gap-3 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
                    <Check size={13} />
                  </span>
                  <span className="text-sm text-white/90">{b}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/register')} data-testid="join-cta"
              className="inline-flex items-center gap-3 px-10 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
              style={{ backgroundColor: '#fff', color: '#000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'none'; }}>
              Apply for Membership <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
